import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useGoBack from '../../hooks/useGoBack';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { styles, colors, radius } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import SearchableSelect from '../../components/SearchableSelect';
import { normalizeDecimalInput, parseDecimal } from '../../utils/decimalInput';
import { getFiyatKaynakRozeti } from '../../utils/fiyatKaynak';
import { logError } from '../../utils/logError';
import { selectionFromRecipe, mergeKanalSecim, selectionToPayload, applyPosOtomatikResult } from '../../utils/receteSatisKanali';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Reçete Oluştur / Düzenle Sayfası
 * - MANAGE_RECIPES yetkisi: tam duzenleme
 * - Yetkisiz: salt okunur görüntüleme (readonly mode)
 */
const ReceteFormPage = () => {
  const navigate = useNavigate();
  // "Geri": uygulama içinden gelindiyse önceki (filtreli) listeye döner, yoksa temiz liste.
  const goBack = useGoBack('/recete/listesi');
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const receteId = searchParams.get('id');
  const isEdit = !!receteId;
  const forceReadonly = searchParams.get('readonly') === '1';
  // 3. arg effectivePermissions ŞART: izin yalnız kişi-bazlı (kullanici_izinleri) atanır,
  // hiçbir rolde (admin hariç) yok → ROLE_DEFINITIONS fallback'i custom atamayı kaçırır.
  const canViewSecret = user && hasPermission(user.role, PERMISSIONS.ACCESS_SECRET_RECIPES, user.effectivePermissions);

  const [form, setForm] = useState({
    recete_kodu: '', ad: '', tip: 'YARI_MAMUL', urun_malzeme_id: null,
    porsiyon_miktar: '', porsiyon_birim: 'KG', lokasyon_id: null,
    marka: '', marka_id: '', açıklama: '', details: [], transfer_edilir: true, anlik_uretim: false,
    cikti_fire_orani: 0,
    gizli: false
  });
  // Bu reçeteyle eşleşmiş POS (MODPOS) ürünleri — salt okunur görünürlük
  const [posEslesmeler, setPosEslesmeler] = useState([]);
  // Gizli reçete + ACCESS_SECRET_RECIPES yok → içerik kilitli: düzenleme kapalı,
  // malzeme tablosu yerine placeholder. Backend zaten details=[] döner + 403 verir
  // (bu yalnız UX kapısı, gerçek güvenlik backend'de — SaaS Anayasası M.5).
  const secretLocked = !!form.gizli && !canViewSecret;
  const canManage = user && hasPermission(user.role, PERMISSIONS.MANAGE_RECIPES) && !forceReadonly && !secretLocked;
  const readonly = !canManage;
  const [malzemeler, setMalzemeler] = useState([]);
  const [receteler, setReceteler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]); // eslint-disable-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [kaydedildi, setKaydedildi] = useState(false);
  const [toplamMaliyet, setToplamMaliyet] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [yeniCiktiOpen, setYeniCiktiOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [yeniCiktiSaving, setYeniCiktiSaving] = useState(false);
  const [yeniCiktiForm, setYeniCiktiForm] = useState({
    kod: '',
    ad: '',
    kategori: 'Yari Mamul',
    birim: 'KG',
    kdv_orani: '8',
    min_stok: '0',
    marka_id: ''
  });
  const [markalar, setMarkalar] = useState([]);
  // Aktif satış kanalları (GET /api/satis-kanallari) + bu reçetenin kanal seçimi.
  // kanalSecim satırı: {kanal_id, ad, tip, secili, aktif, satis_fiyati, sira}
  const [tumKanallar, setTumKanallar] = useState([]);
  const [kanalSecim, setKanalSecim] = useState([]);
  // Reçetenin yüklenen kanal seçimi (selectionFromRecipe çıktısı). tumKanallar ile
  // birleştirilip kanalSecim üretilir — biri diğerinden önce/sonra gelebilir, bu
  // yüzden ayrı effect'te merge edilir. (Latent-bug #353: seed olmazsa re-save siler.)
  const [kanalSecimSeed, setKanalSecimSeed] = useState([]);
  // Gerçek satış özeti (POS) — salt-okunur bilgi paneli, yalnız edit modunda.
  const [satisOzeti, setSatisOzeti] = useState(null);
  // "POS satışına göre ata" tek-tık otomatik atama durumu (in-flight + sonuç bildirimi).
  // posOtoMesaj: backend message'ı sayfa-içi kısa bildirim olarak gösterir (success'te alert YOK).
  const [posOtoLoading, setPosOtoLoading] = useState(false);
  const [posOtoMesaj, setPosOtoMesaj] = useState(null); // { tip: 'ok'|'bilgi'|'hata', metin }

  // Redirect if not edit mode and no manage permission (can't create)
  useEffect(() => {
    if (!isEdit && readonly) {
      alert('Reçete oluşturma yetkiniz yok');
      navigate('/recete/listesi');
    }
  }, [isEdit, readonly, navigate]);

  // Fetch master data
  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      fetch(`${API_URL}/api/malzemeler`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/recipes?limit=200`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/lokasyonlar`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/markalar`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/api/satis-kanallari`, { headers }).then(r => r.json()).catch(() => [])
    ]).then(([m, r, l, mk, sk]) => {
      setMalzemeler(Array.isArray(m) ? m : []);
      setReceteler(Array.isArray(r) ? r : []);
      setLokasyonlar(Array.isArray(l) ? l : []);
      setMarkalar(Array.isArray(mk) ? mk : []);
      setTumKanallar(Array.isArray(sk) ? sk : []);
    });
  }, []);

  // Load existing recipe for edit
  const loadRecete = useCallback(() => {
    if (!receteId) return;
    setLoading(true);
    // POS eşleşmeleri (salt okunur) — paralel çek
    fetch(`${API_URL}/api/recipes/${receteId}/pos-mappings`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setPosEslesmeler(Array.isArray(d) ? d : []))
      .catch(() => setPosEslesmeler([]));
    fetch(`${API_URL}/api/recipes/${receteId}`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.status === 401) {
          logout();
          navigate('/dashboard', { replace: true });
          return null;
        }
        if (r.status === 403) { alert('Bu receteyi görüntüleme yetkiniz yok'); navigate('/recete/listesi'); return null; }
        return r.json();
      })
      .then(data => {
        if (!data) return;
        setForm({
          recete_kodu: data.recete_kodu || '',
          ad: data.ad || '',
          tip: data.tip || 'YARI_MAMUL',
          urun_malzeme_id: data.urun_malzeme_id || null,
          porsiyon_miktar: data.porsiyon_miktar || '',
          porsiyon_birim: data.porsiyon_birim || 'KG',
          lokasyon_id: data.lokasyon_id || null,
          marka: data.marka || '',
          marka_id: data.marka_id || '',
          açıklama: data.açıklama || '',
          transfer_edilir: data.transfer_edilir !== false,
          anlik_uretim: data.anlik_uretim === true,
          cikti_fire_orani: data.cikti_fire_orani || 0,
          gizli: data.gizli === true,
          details: (data.details || []).map(d => ({
            tur: d.alt_recete_id ? 'recete' : 'malzeme',
            malzeme_id: d.malzeme_id || null,
            alt_recete_id: d.alt_recete_id || null,
            miktar: d.miktar || 0,
            birim: d.birim || 'KG',
            fire_orani: d.fire_orani || 0,
            sira_no: d.sira_no || 0,
            açıklama: d.açıklama || ''
          }))
        });
        // Kanal seçimini yüklenen veriden besle (re-save'de silinmesini önler — #353).
        setKanalSecimSeed(selectionFromRecipe(data));
        setLoading(false);
      })
      .catch(err => {
        logError('Reçete yüklenemedi:', err);
        setLoading(false);
      });
  }, [receteId, navigate, logout]);

  useEffect(() => { loadRecete(); }, [loadRecete]);

  // Aktif kanallar + reçetenin seçimi → form satırları. tumKanallar veya seed
  // değişince yeniden birleştirir (sıra bağımsız). Create modunda seed boş → tüm
  // kanallar seçilmemiş satır olur.
  useEffect(() => {
    setKanalSecim(mergeKanalSecim(tumKanallar, kanalSecimSeed));
  }, [tumKanallar, kanalSecimSeed]);

  // Gerçek satış özeti (yalnız mevcut reçete düzenlenirken).
  // Hata/boş veriyi sessizce tolere et: panel "veri yok" gösterir, sayfa çökmez.
  useEffect(() => {
    if (!receteId) {
      setSatisOzeti(null);
      return;
    }
    const headers = getAuthHeaders();
    fetch(`${API_URL}/api/recipes/${receteId}/satis-ozeti`, { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setSatisOzeti(d || null))
      .catch(() => setSatisOzeti(null));
  }, [receteId]);

  // Calculate cost
  const refreshCost = useCallback((rid) => {
    const id = rid || receteId;
    if (!id) return;
    fetch(`${API_URL}/api/recipes/${id}/cost`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setToplamMaliyet(data))
      .catch(() => {});
  }, [receteId]);

  useEffect(() => { refreshCost(); }, [refreshCost]);

  const handleChange = (field, value) => {
    if (readonly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Satış kanalı satırında bir alanı güncelle (secili / satis_fiyati / aktif).
  const handleKanalChange = (kanalId, field, value) => {
    if (readonly) return;
    setKanalSecim(prev => prev.map(k => k.kanal_id === kanalId ? { ...k, [field]: value } : k));
  };

  const addDetail = () => {
    if (readonly) return;
    setForm(prev => ({
      ...prev,
      details: [...prev.details, {
        tur: 'malzeme', malzeme_id: null, alt_recete_id: null, miktar: 0,
        birim: 'KG', fire_orani: 0, sira_no: prev.details.length + 1, açıklama: ''
      }]
    }));
  };

  const updateDetail = (idx, field, value) => {
    if (readonly) return;
    setForm(prev => {
      const newDetails = [...prev.details];
      newDetails[idx] = { ...newDetails[idx], [field]: value };
      if (field === 'malzeme_id' && value) newDetails[idx].alt_recete_id = null;
      if (field === 'alt_recete_id' && value) newDetails[idx].malzeme_id = null;
      return { ...prev, details: newDetails };
    });
  };

  const removeDetail = (idx) => {
    if (readonly) return;
    setForm(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== idx)
    }));
  };

  // Porsiyon miktarini otomatik hesapla: hammaddelerin NET toplami (Σ miktar).
  // Satır firesi YALNIZ maliyeti etkiler (costing: brüt = miktar/(1-fire)); çıktıyı
  // düşürmez. Çıktı kaybı ayrı alandadır: cikti_fire_orani. (Eski "× (1-fire)" fire'ı
  // ikinci kez sayıp birim maliyeti 1/(1-fire) kat şişiriyordu — AÜK-026 bug'ı.)
  useEffect(() => {
    if (readonly) return;
    // TABAK: her zaman 1 porsiyon (sunum birimi)
    if (form.tip === 'TABAK') {
      if (parseFloat(form.porsiyon_miktar) !== 1) {
        setForm(prev => ({ ...prev, porsiyon_miktar: 1, porsiyon_birim: 'PORSIYON' }));
      }
      return;
    }
    if (!form.details || form.details.length === 0) return;
    const toplam = form.details.reduce((acc, d) => acc + (parseFloat(d.miktar) || 0), 0);
    const rounded = Math.round(toplam * 1000) / 1000;
    if (rounded > 0 && rounded !== parseFloat(form.porsiyon_miktar)) {
      setForm(prev => ({ ...prev, porsiyon_miktar: rounded }));
    }
  }, [form.details, form.tip, readonly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reçete kaydı başarılı olduktan sonra satış kanalı atamalarını gönderir.
  // Hata olursa true (başarısız) döner ki çağıran net bir mesaj gösterebilsin.
  const saveKanallar = async (id) => {
    if (!id) return false;
    try {
      const res = await fetch(`${API_URL}/api/recipes/${id}/satis-kanallari`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ kanallar: selectionToPayload(kanalSecim) })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Reçete kaydedildi ancak satış kanalları kaydedilemedi: ${err.detail || res.status}`);
        return true;
      }
      return false;
    } catch (e) {
      alert(`Reçete kaydedildi ancak satış kanalları kaydedilemedi: ${e.message}`);
      return true;
    }
  };

  // "POS satışına göre ata": reçeteyi GERÇEKTEN sattığı kanallara tek tıkla atar
  // (POS ürün adı önekinden türetilir, ADDITIVE — backend mevcut atamaya dokunmaz).
  // Kayıt anında persist olur. Server state'i KOMPLE refetch ETMEYİZ; çünkü kullanıcı
  // bu butona basmadan önce başka bir kanalı (örn. Paket Servis) elle işaretlemiş
  // olabilir — refetch + seed-reset o kaydedilmemiş manuel check'leri yutar (2026-06-22
  // bug raporu). Bunun yerine: yalnız backend'in `atanan` döndürdüğü kanal_id'leri
  // lokal kanalSecim üzerinde secili=true yaparız; manuel check'lere dokunmayız.
  const posOtomatikAta = async () => {
    if (readonly || !receteId || posOtoLoading) return;
    setPosOtoLoading(true);
    setPosOtoMesaj(null);
    try {
      const res = await fetch(`${API_URL}/api/recipes/${receteId}/satis-kanallari/pos-otomatik`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({})
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setPosOtoMesaj({ tip: 'hata', metin: `Atama yapılamadı: ${err.detail || res.status}` });
        setPosOtoLoading(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      const atanan = Array.isArray(data.atanan) ? data.atanan : [];
      // Lokal manuel check'leri KORU. Yalnız backend'in yeni eklediği kanalları işaretle.
      if (atanan.length > 0) {
        setKanalSecim(prev => applyPosOtomatikResult(prev, atanan));
        // Seed'i de sessizce güncelle (sonraki loadRecete refresh'i atanan'ı görmezse
        // bile current state ile uyumlu kalsın; backend zaten persist etti).
        setKanalSecimSeed(prev => {
          const seenIds = new Set((prev || []).map(s => s.kanal_id));
          const additions = atanan
            .filter(a => a.kanal_id != null && !seenIds.has(a.kanal_id))
            .map(a => ({
              kanal_id: a.kanal_id,
              kod: a.kod,
              ad: a.ad,
              tip: null,
              aktif: true,
              satis_fiyati: '',
              sira: 0,
            }));
          return additions.length > 0 ? [...(prev || []), ...additions] : (prev || []);
        });
      }
      const atananKodlar = atanan.map(a => a.kod || a.ad).filter(Boolean);
      if (atananKodlar.length > 0) {
        setPosOtoMesaj({ tip: 'ok', metin: data.message || `Atandı: ${atananKodlar.join(', ')}` });
      } else {
        // atanan boş → ya POS satışı yok ya hepsi zaten atalıydı; backend message açıklar.
        // ÖNEMLI: kanalSecim'e dokunmadık → manuel check'ler yutulmaz.
        setPosOtoMesaj({ tip: 'bilgi', metin: data.message || 'Yeni kanal atanmadı (zaten atalı veya POS satışı yok).' });
      }
    } catch (e) {
      setPosOtoMesaj({ tip: 'hata', metin: `Atama yapılamadı: ${e.message}` });
    }
    setPosOtoLoading(false);
  };

  const handleSave = async () => {
    if (readonly) return;
    if (!form.recete_kodu || !form.ad) {
      alert('Reçete kodu ve adi zorunlu!');
      return;
    }
    if (!form.details || form.details.length === 0) {
      alert('En az bir hammadde eklemelisiniz!');
      return;
    }
    // Faz 2: her reçete bir işletmeye (marka) ait olmalı (backend de 400 ile zorlar).
    if (!form.marka_id) {
      alert('İşletme (marka) seçilmelidir!');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        porsiyon_miktar: form.porsiyon_miktar ? parseFloat(form.porsiyon_miktar) : null,
        cikti_fire_orani: form.cikti_fire_orani ? parseDecimal(form.cikti_fire_orani) : 0,
        urun_malzeme_id: form.urun_malzeme_id ? parseInt(form.urun_malzeme_id) : null,
        lokasyon_id: form.lokasyon_id ? parseInt(form.lokasyon_id) : null,
        marka_id: form.marka_id ? parseInt(form.marka_id, 10) : null,  // F5: '' → null (Optional[int] 422 önle)
        details: form.details.map(d => ({
          malzeme_id: d.malzeme_id ? parseInt(d.malzeme_id) : null,
          alt_recete_id: d.alt_recete_id ? parseInt(d.alt_recete_id) : null,
          miktar: parseFloat(d.miktar) || 0,
          birim: d.birim,
          fire_orani: parseFloat(d.fire_orani) || 0,
          sira_no: d.sira_no,
          açıklama: d.açıklama || null
        }))
      };

      const url = isEdit ? `${API_URL}/api/recipes/${receteId}` : `${API_URL}/api/recipes`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      if (res.status === 403) {
        alert('Reçete duzenleme yetkiniz yok!');
        setSaving(false);
        return;
      }

      if (res.ok) {
        const saved = await res.json().catch(() => null);
        setKaydedildi(true);
        setTimeout(() => setKaydedildi(false), 3000);
        // İlgili cache'leri invalidate et
        invalidateQueries(['recete', 'recipe', 'stok', 'dashboard', 'maliyet', 'transfer']);
        if (isEdit) {
          // Reçete kaydı başarılı → satış kanallarını da kaydet (ek adım).
          await saveKanallar(receteId);
          // Sayfada kal, veriyi ve maliyeti yenile
          loadRecete();
          refreshCost();
        } else {
          // Yeni oluşturuldu → kanalları kaydet, sonra edit moduna geç
          const newId = saved?.id;
          if (newId) {
            await saveKanallar(newId);
            navigate(`/recete/olustur?id=${newId}`, { replace: true });
          } else {
            navigate('/recete/listesi');
          }
        }
      } else {
        const err = await res.json();
        alert(`Hata: ${err.detail || 'Bilinmeyen hata'}`);
      }
    } catch (err) {
      alert(`Hata: ${err.message}`);
    }
    setSaving(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleYeniCiktiOlustur = async () => {
    if (readonly) return;
    if (!yeniCiktiForm.kod || !yeniCiktiForm.ad) {
      alert('Kod ve ad zorunlu!');
      return;
    }

    setYeniCiktiSaving(true);
    try {
      const payload = {
        kod: yeniCiktiForm.kod.trim(),
        ad: yeniCiktiForm.ad.trim(),
        kategori: yeniCiktiForm.kategori,
        birim: yeniCiktiForm.birim,
        kdv_orani: parseFloat(yeniCiktiForm.kdv_orani) || 8,
        min_stok: parseFloat(yeniCiktiForm.min_stok) || 0,
        marka_id: yeniCiktiForm.marka_id ? parseInt(yeniCiktiForm.marka_id, 10) : null,
      };

      const res = await fetch(`${API_URL}/api/malzemeler`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Cikti urun olusturulamadi');
        setYeniCiktiSaving(false);
        return;
      }

      const created = await res.json();
      if (created && created.id) {
        setMalzemeler(prev => [...prev, created].sort((a, b) => {
          const left = `${a.kod || ''} ${a.ad || ''}`.trim();
          const right = `${b.kod || ''} ${b.ad || ''}`.trim();
          return left.localeCompare(right, 'tr');
        }));
        setForm(prev => ({ ...prev, urun_malzeme_id: created.id }));
      } else {
        const listRes = await fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() });
        const list = await listRes.json().catch(() => []);
        setMalzemeler(Array.isArray(list) ? list : []);
      }

      setYeniCiktiForm({
        kod: '',
        ad: '',
        kategori: 'Yari Mamul',
        birim: 'KG',
        kdv_orani: '8',
        min_stok: '0',
        marka_id: ''
      });
      setYeniCiktiOpen(false);
    } catch (err) {
      alert(`Hata: ${err.message}`);
    }
    setYeniCiktiSaving(false);
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Yükleniyor...</div>;
  }

  const costBreakdown = Array.isArray(toplamMaliyet?.breakdown) ? toplamMaliyet.breakdown : [];

  const inputStyle = {
    width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    fontSize: '13px', boxSizing: 'border-box',
    ...(readonly ? { background: colors.background, cursor: 'not-allowed' } : {})
  };

  const selectStyle = {
    width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px',
    ...(readonly ? { background: colors.background, cursor: 'not-allowed', pointerEvents: 'none' } : {})
  };

  // eslint-disable-next-line no-unused-vars
  const miniInputStyle = {
    width: '100%',
    padding: '6px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '12px',
    boxSizing: 'border-box'
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
        color: 'white', padding: '15px 25px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={goBack}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '6px 12px', borderRadius: radius.sm, cursor: 'pointer' }}>
            Geri
          </button>
          <h1 style={{ margin: 0, fontSize: '20px' }}>
            {readonly
              ? `Reçete: ${form.recete_kodu}`
              : isEdit ? `Reçete Düzenle: ${form.recete_kodu}` : 'Yeni Reçete'}
          </h1>
          {readonly && (
            <span style={{ fontSize: '12px', background: 'rgba(255,200,0,0.3)', padding: '4px 12px', borderRadius: radius.lg }}>
              Salt Okunur
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {kaydedildi && (
            <span style={{ fontSize: '13px', background: 'rgba(40,167,69,0.3)', padding: '6px 14px', borderRadius: radius.sm, animation: 'fadeIn 0.3s' }}>
              ✅ Kaydedildi
            </span>
          )}
          {!readonly && (
          <button onClick={handleSave} disabled={saving}
            style={{
              background: colors.surface, color: styles.primary, border: 'none',
              padding: '8px 20px', borderRadius: radius.sm, fontWeight: 'bold', cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        )}
        </div>
      </div>

      <div style={{ padding: '20px', maxWidth: '1200px' }}>
        {/* Header Form */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>Genel Bilgiler</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>Reçete Kodu *</label>
              <input value={form.recete_kodu} onChange={e => handleChange('recete_kodu', e.target.value)}
                disabled={isEdit || readonly} readOnly={readonly}
                style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>Reçete Adi *</label>
              <input value={form.ad} onChange={e => handleChange('ad', e.target.value)}
                readOnly={readonly}
                style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>Tip</label>
              <select value={form.tip} onChange={e => {
                const yeniTip = e.target.value;
                handleChange('tip', yeniTip);
                // Yarı mamulden çıkınca bayat 'yerinde üretim' bayrağı payload'da kalmasın
                if (yeniTip !== 'YARI_MAMUL' && !form.transfer_edilir) {
                  handleChange('transfer_edilir', true);
                }
                // Aynı şekilde anlık üretim bayrağı yalnız YARI_MAMUL için anlamlı
                if (yeniTip !== 'YARI_MAMUL' && form.anlik_uretim) {
                  handleChange('anlik_uretim', false);
                }
              }}
                disabled={readonly}
                style={selectStyle}>
                <option value="YARI_MAMUL">Yari Mamul (Imalathane)</option>
                <option value="TABAK">Tabak (Sube)</option>
              </select>
            </div>
            {form.tip === 'YARI_MAMUL' && (
              <div>
                <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>Tedarik Şekli</label>
                <select
                  value={form.transfer_edilir ? 'TRANSFER' : 'YERINDE'}
                  onChange={e => handleChange('transfer_edilir', e.target.value === 'TRANSFER')}
                  disabled={readonly}
                  style={selectStyle}
                  title="Yerinde üretim: ürün kullanıldığı markada üretilir; maliyette transfer fiyatı değil üretim maliyeti kullanılır">
                  <option value="TRANSFER">İmalathaneden transfer edilir</option>
                  <option value="YERINDE">Yerinde üretilir (transfer yok)</option>
                </select>
              </div>
            )}
            {form.tip === 'YARI_MAMUL' && (
              <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '4px' }}>
                <input
                  type="checkbox"
                  id="anlik_uretim"
                  checked={!!form.anlik_uretim}
                  onChange={e => handleChange('anlik_uretim', e.target.checked)}
                  disabled={readonly}
                  style={{ width: '16px', height: '16px', cursor: readonly ? 'default' : 'pointer' }}
                  title="Stoklanmayan, sipariş anında üretilen yarı mamul (patates kızartması, dondurma). POS satışında bu yarı mamul yerine ham maddeleri stoktan düşülür." />
                <label htmlFor="anlik_uretim" style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold', cursor: readonly ? 'default' : 'pointer' }}>
                  Anlık üretilir / stoklanmaz (POS satışı ham maddeden düşer)
                </label>
              </div>
            )}
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>
                {form.tip === 'TABAK'
                  ? <>Sunum Birimi <span style={{ fontSize: '10px', color: colors.textTertiary, fontWeight: 'normal' }}>(1 tabak = 1 porsiyon)</span></>
                  : <>Üretim Çıktısı <span style={{ fontSize: '10px', color: colors.textTertiary, fontWeight: 'normal' }}>(otomatik — hammadde net toplamı; satır firesi yalnızca maliyete yansır)</span></>
                }
              </label>
              <input type="number" step="0.001" value={form.tip === 'TABAK' ? 1 : form.porsiyon_miktar}
                readOnly
                title={form.tip === 'TABAK' ? '1 tabak = 1 porsiyon (sabit)' : 'Hammadde toplamından fire düşülerek otomatik hesaplanır'}
                style={{ ...inputStyle, background: colors.background, color: colors.textSecondary }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>Birim</label>
              <select value={form.porsiyon_birim} onChange={e => handleChange('porsiyon_birim', e.target.value)}
                disabled={readonly}
                style={selectStyle}>
                <option value="KG">KG</option>
                <option value="LT">LT</option>
                <option value="AD">AD</option>
                <option value="PORSIYON">PORSIYON</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>
                Çıktı Firesi %
                <span title="Üretim sonrası nihai üründe oluşan kayıp. Birim maliyete yansır; Üretim Kaydı'nda stoğa net (sağlam) miktar yazılır."
                      style={{ marginLeft: 4, cursor: 'help', color: '#999' }}>ⓘ</span>
              </label>
              <input
                type="text" inputMode="decimal"
                min="0" max="99"
                value={form.cikti_fire_orani ?? 0}
                onChange={e => handleChange('cikti_fire_orani', normalizeDecimalInput(e.target.value))}
                readOnly={readonly}
                placeholder="0"
                style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>İşletme (Marka) *</label>
              <select value={form.marka_id || ''} onChange={e => handleChange('marka_id', e.target.value)}
                disabled={readonly}
                required
                style={{ ...inputStyle, ...(!form.marka_id && !readonly ? { borderColor: colors.danger || '#dc2626' } : {}) }}>
                <option value="">Seçiniz…</option>
                {markalar.map(m => <option key={m.id} value={m.id}>{m.ad}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: '12px', color: colors.textSecondary, fontWeight: 'bold' }}>Açıklama</label>
              <input value={form.açıklama} onChange={e => handleChange('açıklama', e.target.value)}
                readOnly={readonly}
                style={inputStyle} />
            </div>
          </div>

          {/* Satış Kanalları — reçete hangi kanallarda satılıyor + opsiyonel fiyat.
              Kanallar API'den (GET /api/satis-kanallari) gelir (M.1, hardcode yok). */}
          <div style={{ marginTop: '15px', border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: colors.textSecondary }}>
                Satış Kanalları {kanalSecim.length > 0 && `(${kanalSecim.filter(k => k.secili).length}/${kanalSecim.length})`}
              </div>
              {/* POS satışına göre tek tık otomatik atama — yalnız mevcut reçete + düzenleme yetkisi.
                  Backend MANAGE_RECIPES enforce eder (Anayasa M.5); bu yalnız UX kapısı. */}
              {isEdit && !readonly && (
                <button
                  type="button"
                  onClick={posOtomatikAta}
                  disabled={posOtoLoading}
                  title="Reçetenin POS'ta gerçekten sattığı kanalları otomatik işaretler ve kaydeder (mevcut seçimi korur)"
                  style={{
                    border: `1px solid ${styles.primary}`, background: colors.surface, color: styles.primary,
                    borderRadius: radius.sm, padding: '4px 10px', fontSize: '12px', fontWeight: 'bold',
                    cursor: posOtoLoading ? 'wait' : 'pointer', opacity: posOtoLoading ? 0.6 : 1, whiteSpace: 'nowrap'
                  }}>
                  {posOtoLoading ? '⏳ Atanıyor…' : '🔄 POS satışına göre ata'}
                </button>
              )}
              {posOtoMesaj && (
                <span style={{
                  fontSize: '12px', fontWeight: '600',
                  color: posOtoMesaj.tip === 'hata' ? (colors.danger || '#c62828')
                    : posOtoMesaj.tip === 'ok' ? '#2e7d32' : colors.textSecondary
                }}>
                  {posOtoMesaj.metin}
                </span>
              )}
            </div>
            {kanalSecim.length === 0 ? (
              <div style={{ fontSize: '12px', color: colors.textTertiary }}>
                Tanımlı aktif satış kanalı yok. Ayarlar'dan ekleyebilirsiniz.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {kanalSecim.map(k => (
                  <div key={k.kanal_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', minWidth: '200px', cursor: readonly ? 'default' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={k.secili}
                        disabled={readonly}
                        onChange={e => handleKanalChange(k.kanal_id, 'secili', e.target.checked)} />
                      <span>{k.ad}</span>
                      {k.tip && (
                        <span style={{ fontSize: '11px', color: colors.textTertiary, padding: '1px 6px', borderRadius: radius.sm, background: colors.background }}>
                          {k.tip}
                        </span>
                      )}
                      {k.kanalPasif && (
                        <span
                          title="Bu kanal Ayarlar'dan pasifleştirilmiş ama reçetede atalı. İşareti kaldırırsanız atama silinir."
                          style={{ fontSize: '11px', color: colors.danger || '#dc2626', padding: '1px 6px', borderRadius: radius.sm, background: colors.background, fontWeight: 'bold' }}>
                          pasif
                        </span>
                      )}
                    </label>
                    {k.secili && (
                      <input
                        type="text" inputMode="decimal"
                        value={k.satis_fiyati ?? ''}
                        onChange={e => handleKanalChange(k.kanal_id, 'satis_fiyati', normalizeDecimalInput(e.target.value))}
                        readOnly={readonly}
                        placeholder="Satış fiyatı (ops.)"
                        style={{ ...inputStyle, width: '160px' }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gerçek satış özeti — POS gözlemsel verisi: toplam adet/ciro,
              lokasyon kırılımı ve POS ürün adından türetilen gerçek kanal kırılımı. */}
          {isEdit && (
            <div style={{ marginTop: '15px', border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '12px 16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: colors.textSecondary, marginBottom: '8px' }}>
                Gerçek Satış Özeti (POS)
              </div>
              {!satisOzeti ? (
                <div style={{ fontSize: '12px', color: colors.textTertiary }}>
                  Satış verisi yok.
                </div>
              ) : (satisOzeti.toplam_adet || 0) <= 0 ? (
                <div style={{ fontSize: '12px', color: colors.textTertiary }}>
                  Bu reçete için POS satışı bulunamadı.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }}>Toplam Adet</span>
                      <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
                        {Number(satisOzeti.toplam_adet).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', borderLeft: `2px solid ${colors.border}`, paddingLeft: '24px' }}>
                      <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }}>Toplam Ciro</span>
                      <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#2e7d32' }}>
                        {Number(satisOzeti.toplam_ciro || 0).toFixed(2)} TL
                      </span>
                    </div>
                  </div>

                  {/* Lokasyon kırılımı — ürünün gerçekten sattığı şubeler */}
                  {Array.isArray(satisOzeti.lokasyon_kirilim) && satisOzeti.lokasyon_kirilim.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.textSecondary, marginBottom: '6px' }}>Lokasyon Kırılımı</div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Lokasyon</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Marka</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Adet</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ciro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {satisOzeti.lokasyon_kirilim.map((l, i) => (
                            <tr key={l.lokasyon_id != null ? `lok-${l.lokasyon_id}` : `lok-i-${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <td style={{ padding: '6px 8px' }}>{l.lokasyon_ad || `#${l.lokasyon_id}`}</td>
                              <td style={{ padding: '6px 8px', color: colors.textSecondary }}>{l.marka_ad || '-'}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(l.adet || 0).toLocaleString('tr-TR')}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(l.ciro || 0).toFixed(2)} TL</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Kanal bazlı satış — POS ürün adı önekinden TÜRETİLEN gerçek kırılım (ürün düzeyinde). */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: colors.textSecondary, marginBottom: '6px' }}>
                      Kanal Bazlı Satış
                      <span style={{ fontWeight: 'normal', fontSize: '11px', color: colors.textTertiary, marginLeft: '6px' }}>
                        (POS ürün adından — gerçek)
                      </span>
                    </div>
                    {Array.isArray(satisOzeti.kanal_satis_kirilim) && satisOzeti.kanal_satis_kirilim.length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left' }}>Kanal</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Adet</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ciro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {satisOzeti.kanal_satis_kirilim.map((c, i) => (
                            <tr key={c.kanal_kod != null ? `ksk-${c.kanal_kod}` : `ksk-i-${i}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                              <td style={{ padding: '6px 8px' }}>{c.kanal_ad || c.kanal_kod || '-'}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(c.adet || 0).toLocaleString('tr-TR')}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(c.ciro || 0).toFixed(2)} TL</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ fontSize: '12px', color: colors.textTertiary }}>Kanal kırılımı yok.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Eşleşen POS (MODPOS) ürünleri — salt okunur görünürlük (edit modunda) */}
          {isEdit && (
            <div style={{ marginTop: '15px', border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '12px 16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: colors.textSecondary, marginBottom: '8px' }}>
                Eşleşen POS Ürünleri ({posEslesmeler.length})
              </div>
              {posEslesmeler.length === 0 ? (
                <div style={{ fontSize: '12px', color: colors.textTertiary }}>
                  Bu reçete henüz bir POS ürünüyle eşleşmemiş.
                  <span onClick={() => navigate('/entegrasyon/eslestirme')}
                    style={{ color: styles.primary, cursor: 'pointer', marginLeft: 6, textDecoration: 'underline' }}>
                    Eşleştirme Merkezi
                  </span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {posEslesmeler.map(p => (
                    <span key={p.id} title={p.aktif ? 'Aktif eşleşme' : 'Pasif eşleşme'}
                      style={{ padding: '3px 10px', borderRadius: radius.lg, fontSize: '12px',
                        background: p.aktif ? '#e8f5e9' : '#f1f3f5',
                        color: p.aktif ? '#2e7d32' : colors.textTertiary }}>
                      🔗 {p.pos_urun_adi}{!p.aktif && ' (pasif)'}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Cost summary for edit mode */}
          {toplamMaliyet && (
            <div style={{ marginTop: '15px', borderRadius: radius.md, overflow: 'hidden', border: '1px solid #c8e6c9' }}>
              <div style={{ padding: '14px 16px', background: '#e8f5e9', display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }}>
                    Toplam Üretim Maliyeti {toplamMaliyet.porsiyon_miktar ? `(${toplamMaliyet.porsiyon_miktar} ${toplamMaliyet.porsiyon_birim || form.porsiyon_birim})` : ''}
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#2e7d32' }}>
                    {toplamMaliyet.toplam_maliyet?.toFixed(2)} TL
                  </span>
                </div>
                {toplamMaliyet.porsiyon_maliyet != null && (
                  <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #a5d6a7', paddingLeft: '24px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }}>
                      {form.tip === 'TABAK' ? 'Porsiyon Maliyeti' : 'Birim Maliyet'}
                    </span>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#1565c0' }}>
                      {toplamMaliyet.porsiyon_maliyet?.toFixed(2)} TL/{toplamMaliyet.porsiyon_birim || form.porsiyon_birim}
                    </span>
                  </div>
                )}
                {/* Yenilebilir FC — Hakan'ın bakacağı endüstri benchmark (ambalaj hariç) */}
                {toplamMaliyet.yenilebilir_fc_ratio != null && (
                  <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #a5d6a7', paddingLeft: '24px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }} title="Sadece yenilebilir hammadde / POS — F&B endüstri benchmark">
                      Food Cost (yenilebilir)
                    </span>
                    <span style={{
                      fontSize: '20px', fontWeight: 'bold',
                      color: toplamMaliyet.yenilebilir_fc_ratio < 28 ? '#2e7d32' : toplamMaliyet.yenilebilir_fc_ratio < 35 ? '#f57f17' : '#c62828'
                    }}>
                      %{toplamMaliyet.yenilebilir_fc_ratio?.toFixed(1)}
                    </span>
                  </div>
                )}
                {/* Ambalaj oranı — paket servis görünürlüğü, sadece >0 ise */}
                {toplamMaliyet.ambalaj_oran != null && toplamMaliyet.ambalaj_oran > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #a5d6a7', paddingLeft: '24px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }} title="Ambalaj kalemleri / POS — paket servis maliyeti payı">
                      Ambalaj
                    </span>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#3f51b5' }}>
                      %{toplamMaliyet.ambalaj_oran?.toFixed(1)}
                    </span>
                  </div>
                )}
                {/* Toplam Cost — paket dahil tam karlılık (sadece ambalaj varsa ek rozet) */}
                {toplamMaliyet.food_cost_ratio != null && toplamMaliyet.ambalaj_oran > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #a5d6a7', paddingLeft: '24px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }} title="(Yenilebilir + Ambalaj) / POS — paket dahil toplam maliyet">
                      Toplam Cost
                    </span>
                    <span style={{
                      fontSize: '20px', fontWeight: 'bold',
                      color: toplamMaliyet.food_cost_ratio < 35 ? '#2e7d32' : toplamMaliyet.food_cost_ratio < 50 ? '#f57f17' : '#c62828'
                    }}>
                      %{toplamMaliyet.food_cost_ratio?.toFixed(1)}
                    </span>
                  </div>
                )}
                {/* Backward-compat: yenilebilir alanı yoksa eski tek metrik (deploy senkronizasyonu için) */}
                {toplamMaliyet.food_cost_ratio != null && toplamMaliyet.yenilebilir_fc_ratio == null && (
                  <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #a5d6a7', paddingLeft: '24px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }}>Food Cost</span>
                    <span style={{
                      fontSize: '20px', fontWeight: 'bold',
                      color: toplamMaliyet.food_cost_ratio < 28 ? '#2e7d32' : toplamMaliyet.food_cost_ratio < 35 ? '#f57f17' : '#c62828'
                    }}>
                      %{toplamMaliyet.food_cost_ratio?.toFixed(1)}
                    </span>
                  </div>
                )}
                {toplamMaliyet.satis_fiyati != null && (
                  <div style={{ display: 'flex', flexDirection: 'column', borderLeft: '2px solid #a5d6a7', paddingLeft: '24px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '2px' }}>
                      Satış Fiyatı
                      <span style={{
                        marginLeft: '6px', fontSize: '9px', padding: '1px 6px',
                        borderRadius: '8px', background: '#e3f2fd', color: '#1565c0'
                      }}>POS</span>
                    </span>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#555' }}>
                      {toplamMaliyet.satis_fiyati?.toFixed(2)} TL
                    </span>
                  </div>
                )}
              </div>
              {/* Birim uyumsuzluk uyarilari */}
              {toplamMaliyet.birim_uyarilari && toplamMaliyet.birim_uyarilari.length > 0 && (
                <div style={{ padding: '8px 16px', background: '#fff3e0', borderTop: '1px solid #ffe0b2', fontSize: '12px', color: '#e65100' }}>
                  {toplamMaliyet.birim_uyarilari.map((u, i) => (
                    <div key={i} style={{ marginBottom: i < toplamMaliyet.birim_uyarilari.length - 1 ? '4px' : 0 }}>
                      {u.mesaj}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail Lines */}
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: '20px' }}>
          {secretLocked ? (
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔒</div>
              <h3 style={{ margin: '0 0 6px', color: '#333', fontSize: '16px' }}>Gizli Tarif</h3>
              <p style={{ color: colors.textSecondary, fontSize: '13px', margin: 0, maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
                Bu reçetenin içeriği gizlidir. Görüntülemek için yetki gerekir — yöneticinizle iletişime geçin.
              </p>
            </div>
          ) : (
          <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Icerik Satirlari ({form.details.length})</h3>
            {!readonly && (
              <button onClick={addDetail}
                style={{ background: styles.primary, color: 'white', border: 'none', padding: '6px 16px', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px' }}>
                + Satir Ekle
              </button>
            )}
          </div>

          {form.details.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#888' }}>
              {readonly ? 'Bu recetede icerik satiri bulunmuyor.' : 'Henuz satir eklenmemis. "Satir Ekle" butonuna tiklayin.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '8px', textAlign: 'center', width: '40px' }}>#</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Tur</th>
                  <th style={{ padding: '8px', textAlign: 'left', minWidth: '250px' }}>Malzeme / Alt Reçete</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '100px' }}>Miktar</th>
                  <th style={{ padding: '8px', textAlign: 'center', width: '70px' }}>Birim</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '70px' }}>Fire %</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '110px' }}>Birim Fiyat</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '120px' }}>Satir Maliyeti</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: '80px' }}>Pay %</th>
                  {!readonly && <th style={{ padding: '8px', textAlign: 'center', width: '50px' }}></th>}
                </tr>
              </thead>
              <tbody>
                {form.details.map((d, idx) => {
                  const isAltRecete = d.tur === 'recete';
                  const satirMaliyet = costBreakdown[idx] || null;
                  const satirPay = satirMaliyet && toplamMaliyet?.toplam_maliyet
                    ? (satirMaliyet.satir_maliyet / toplamMaliyet.toplam_maliyet) * 100
                    : null;
                  // Birim fiyat kaynağı ibaresi: TF (transfer) / SAS (iç satış) / SAS~ (tahmini)
                  const fiyatRozet = getFiyatKaynakRozeti(satirMaliyet?.kaynak);
                  // For readonly: resolve names
                  const malzemeObj = d.malzeme_id ? malzemeler.find(m => m.id === parseInt(d.malzeme_id)) : null;
                  const receteObj = d.alt_recete_id ? receteler.find(r => r.id === parseInt(d.alt_recete_id)) : null;

                  return (
                    <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ padding: '6px', textAlign: 'center', color: '#888' }}>{idx + 1}</td>
                      <td style={{ padding: '6px' }}>
                        {readonly ? (
                          <span style={{ fontSize: '11px', color: colors.textSecondary }}>{isAltRecete ? 'Alt Reçete' : 'Malzeme'}</span>
                        ) : (
                          <select value={d.tur || 'malzeme'}
                            onChange={e => {
                              const yeniTur = e.target.value;
                              setForm(prev => {
                                const newDetails = [...prev.details];
                                newDetails[idx] = {
                                  ...newDetails[idx],
                                  tur: yeniTur,
                                  malzeme_id: yeniTur === 'malzeme' ? newDetails[idx].malzeme_id : null,
                                  alt_recete_id: yeniTur === 'recete' ? newDetails[idx].alt_recete_id : null,
                                };
                                return { ...prev, details: newDetails };
                              });
                            }}
                            style={{ padding: '4px', border: `1px solid ${colors.border}`, borderRadius: '3px', fontSize: '11px' }}>
                            <option value="malzeme">Malzeme</option>
                            <option value="recete">Alt Reçete</option>
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '6px' }}>
                        {readonly ? (
                          <span style={{ fontSize: '12px' }}>
                            {isAltRecete
                              ? (receteObj ? `${receteObj.recete_kodu} - ${receteObj.ad}` : `Reçete #${d.alt_recete_id}`)
                              : (malzemeObj ? `${malzemeObj.kod} - ${malzemeObj.ad}` : `Malzeme #${d.malzeme_id}`)}
                          </span>
                        ) : isAltRecete ? (
                          <SearchableSelect
                            compact
                            options={receteler.filter(r => r.id !== receteId).map(r => ({ value: r.id, label: `${r.recete_kodu} - ${r.ad}` }))}
                            value={d.alt_recete_id || ''}
                            onChange={val => updateDetail(idx, 'alt_recete_id', val)}
                            placeholder="Alt Reçete Ara..."
                          />
                        ) : (
                          <SearchableSelect
                            compact
                            options={malzemeler.map(m => ({ value: m.id, label: `${m.kod} - ${m.ad}` }))}
                            value={d.malzeme_id || ''}
                            onChange={val => updateDetail(idx, 'malzeme_id', val)}
                            placeholder="Malzeme Ara..."
                          />
                        )}
                      </td>
                      <td style={{ padding: '6px' }}>
                        {readonly ? (
                          <span style={{ float: 'right' }}>{d.miktar}</span>
                        ) : (
                          <input type="number" step="0.001" value={d.miktar}
                            onChange={e => updateDetail(idx, 'miktar', e.target.value)}
                            style={{ width: '100%', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: '3px', textAlign: 'right', fontSize: '12px', boxSizing: 'border-box' }} />
                        )}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'center' }}>
                        {readonly ? d.birim : (
                          <select value={d.birim} onChange={e => updateDetail(idx, 'birim', e.target.value)}
                            style={{ width: '100%', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: '3px', fontSize: '11px' }}>
                            <option value="KG">KG</option>
                            <option value="LT">LT</option>
                            <option value="AD">AD</option>
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right' }}>
                        {readonly ? `%${d.fire_orani}` : (
                          <input type="number" step="0.1" value={d.fire_orani}
                            onChange={e => updateDetail(idx, 'fire_orani', e.target.value)}
                            style={{ width: '100%', padding: '4px', border: `1px solid ${colors.border}`, borderRadius: '3px', textAlign: 'right', fontSize: '12px', boxSizing: 'border-box' }} />
                        )}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', fontFamily: 'monospace',
                        color: fiyatRozet ? fiyatRozet.renk : undefined
                      }} title={satirMaliyet ? `Kaynak: ${fiyatRozet ? fiyatRozet.ipucu : satirMaliyet.kaynak === 'RECETE' ? 'Reçete Üretim Maliyeti' : satirMaliyet.kaynak === 'PARCALAMA' ? 'Parçalama Çıktısı' : satirMaliyet.kaynak}` : ''}>
                        {satirMaliyet ? `${satirMaliyet.birim_fiyat?.toFixed(2)} TL` : '-'}
                        {fiyatRozet && <span style={{ fontSize: '9px', marginLeft: '2px', color: fiyatRozet.renk }}>{fiyatRozet.etiket}</span>}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                        {satirMaliyet ? `${satirMaliyet.satir_maliyet?.toFixed(2)} TL` : '-'}
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', color: '#2e7d32' }}>
                        {satirPay !== null ? `%${satirPay.toFixed(1)}` : '-'}
                      </td>
                      {!readonly && (
                        <td style={{ padding: '6px', textAlign: 'center' }}>
                          <button onClick={() => removeDetail(idx)} title="Sil"
                            style={{ background: '#ffebee', border: 'none', borderRadius: '3px', padding: '4px 8px', cursor: 'pointer', color: '#c62828' }}>
                            Sil
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceteFormPage;
